import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Sparkles, RefreshCw, Brain, TrendingUp, AlertTriangle, CheckCircle2, Lightbulb } from 'lucide-react';
import { insightsAPI, analyticsAPI } from '../services/api';
import { toast } from '../hooks/use-toast';

const AIInsights = () => {
  const [insights, setInsights] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [insightsData, analyticsData] = await Promise.all([
        insightsAPI.getAll(10).catch(() => []),
        analyticsAPI.getSummary().catch(() => null)
      ]);
      setInsights(insightsData);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleGenerateInsight = async () => {
    setGenerating(true);
    try {
      const newInsight = await insightsAPI.generate();
      setInsights([newInsight, ...insights]);
      toast({
        title: "New Insight Generated!",
        description: "AI has analyzed your data and provided a new recommendation."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to generate insight",
        variant: "destructive"
      });
    }
    setGenerating(false);
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case 'low':
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      default:
        return <Lightbulb className="w-5 h-5 text-blue-500" />;
    }
  };

  const getPriorityStyles = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800';
      case 'low':
        return 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800';
      default:
        return 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800';
    }
  };

  const getCategoryColor = (category) => {
    switch (category?.toLowerCase()) {
      case 'progress':
        return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400';
      case 'nutrition':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400';
      case 'medication':
        return 'bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-400';
      case 'alert':
        return 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">AI Insights</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Personalized health recommendations powered by AI</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="dark:border-gray-600 dark:text-gray-300">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={handleGenerateInsight}
            className="bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 shadow-lg hover:shadow-xl transition-all"
            disabled={generating}
          >
            {generating ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> Generate Insight</>
            )}
          </Button>
        </div>
      </div>

      {/* Analytics Summary */}
      {analytics && (
        <div className="grid md:grid-cols-4 gap-4">
          <Card className="shadow-md border-0 dark:bg-gray-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Avg Glucose</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">{analytics.average_glucose || '--'} mg/dL</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-md border-0 dark:bg-gray-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">In Range</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">{analytics.in_range_percentage || 0}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-md border-0 dark:bg-gray-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                  <Brain className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Glucose Logs</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">{analytics.total_glucose_logs || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-md border-0 dark:bg-gray-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-violet-100 dark:bg-violet-900/30 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Insights</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">{insights.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Insights List */}
      <Card className="shadow-md border-0 dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 dark:text-white">
            <Brain className="w-5 h-5" />
            Your Personalized Insights
          </CardTitle>
          <CardDescription className="dark:text-gray-400">
            AI-generated recommendations based on your health data
          </CardDescription>
        </CardHeader>
        <CardContent>
          {insights.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Sparkles className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No insights yet</p>
              <p className="text-sm mt-1">Log more data and click "Generate Insight" to get AI recommendations</p>
              <Button 
                onClick={handleGenerateInsight}
                className="mt-4 bg-gradient-to-r from-violet-500 to-purple-500"
                disabled={generating}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Generate Your First Insight
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {insights.map((insight, index) => (
                <div 
                  key={insight.id || index} 
                  className={`p-4 rounded-lg border ${getPriorityStyles(insight.priority)}`}
                >
                  <div className="flex items-start gap-3">
                    {getPriorityIcon(insight.priority)}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColor(insight.category)}`}>
                          {insight.category || 'General'}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(insight.date).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <p className="text-gray-700 dark:text-gray-300">{insight.generated_text}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tips Section */}
      <Card className="shadow-md border-0 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 dark:text-white">
            <Lightbulb className="w-5 h-5 text-yellow-500" />
            Tips for Better Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-white/50 dark:bg-gray-700/50 rounded-lg">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Log Regularly</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Record your glucose readings at consistent times for more accurate insights.
              </p>
            </div>
            <div className="p-4 bg-white/50 dark:bg-gray-700/50 rounded-lg">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Track Meals</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Log your meals to help AI understand how food affects your glucose levels.
              </p>
            </div>
            <div className="p-4 bg-white/50 dark:bg-gray-700/50 rounded-lg">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Be Consistent</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                The more data you provide, the more personalized your insights will become.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AIInsights;
